import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { usePlanOverageUsageData } from './usePlanOverageUsageData';
import { OveragesTooltip } from 'assets/js/pages/account-settings/account-about/givebutter-plus/OveragesTooltip';
import { RecommendedArrow } from 'assets/js/pages/account-settings/account-about/givebutter-plus/PlanRowLabel';
import { useSubscriptionPlans } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useSubscriptionPlans';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { getCostDifferenceBetweenPlans } from 'customHooks/useRecommendedPlanComparison';
import { twJoin, twMerge } from 'tailwind-merge';
import { GivebutterPlusPlan } from 'types/domain';

interface SubscriptionPlanLineItemsDetailedProps {
    plan: GivebutterPlusPlan;
    showAnnualPricing: boolean;
}
const SubscriptionPlanLineItemsDetailed = ({
    plan,
    showAnnualPricing,
}: SubscriptionPlanLineItemsDetailedProps) => {
    const { validGivebutterPlusPlan } = useCurrentAccount();
    const { data: plans } = useSubscriptionPlans();
    const { isGracePeriod } = usePlanOverageUsageData();

    if (!validGivebutterPlusPlan) {
        return null;
    }

    const currentTier = validGivebutterPlusPlan.tier;

    const currentPlan = plans.find(plan => plan.contacts_max === currentTier.max_contacts);

    if (!currentPlan) {
        return null;
    }

    const currentlyOnAnnualPlan = currentTier.interval === 'year';

    const currentBillingCycleCost = currentlyOnAnnualPlan
        ? currentPlan.billing.yearly_billing_cycle
        : currentPlan.billing.monthly_billing_cycle;
    const newBillingCycleCost = showAnnualPricing
        ? plan.billing.yearly_billing_cycle
        : plan.billing.monthly_billing_cycle;

    const savingsWithNewPlan = getCostDifferenceBetweenPlans(
        currentBillingCycleCost,
        newBillingCycleCost,
        showAnnualPricing,
    );

    const billingCycleCost = showAnnualPricing
        ? plan.billing.yearly_billing_cycle
        : plan.billing.monthly_billing_cycle;

    return (
        <div className="flex flex-col gap-2 text-sm">
            {currentTier && (
                <div className="font-medium">
                    <p className="flex flex-col">
                        <span className="text-gray-600">
                            Current cost for {currentTier.current_contacts_count} contacts:
                        </span>
                        <span className={twMerge(savingsWithNewPlan > 0 && 'text-warning-600')}>
                            <CurrencySymbol />
                            {maskDecimal(currentPlan.total_monthly)} /mo
                        </span>
                    </p>
                </div>
            )}
            <div className="flex w-full flex-col gap-4 rounded-lg bg-gray-50 p-4">
                {plan.is_recommend && <RecommendedArrow />}
                <div className="flex flex-col gap-2">
                    <p className="font-medium text-gray-600">{`New ${showAnnualPricing ? 'year' : 'month'}ly plan`}</p>
                    <div className="flex flex-col gap-1">
                        <div className="flex justify-between gap-1 text-nowrap text-sm">
                            <span className="shrink-0 text-gray-600">
                                {maskWholeNumber(plan.contacts_max)} contacts
                            </span>
                            <span className="font-medium">
                                <CurrencySymbol />
                                {maskDecimal(plan.monthly_price)}
                            </span>
                        </div>
                        {plan.overage_pack_count > 0 && (
                            <div>
                                <div className="flex justify-between">
                                    <span className="flex shrink-0 whitespace-pre-wrap text-gray-600">
                                        Extra 100 contacts &times; {plan.overage_pack_count}{' '}
                                        <OveragesTooltip />
                                    </span>
                                    <span className="flex gap-1">
                                        <span className={twJoin(isGracePeriod && 'line-through')}>
                                            <CurrencySymbol />
                                            {maskDecimal(plan.extra_contacts_due_price_monthly)}
                                        </span>
                                        {isGracePeriod && <span>$0.00</span>}
                                    </span>
                                </div>
                                {isGracePeriod && (
                                    <p className="text-right text-xs text-gray-500">
                                        No overages for your first month
                                    </p>
                                )}
                            </div>
                        )}
                        {showAnnualPricing && (
                            <div
                                className={
                                    'text-brand-violet-primary flex justify-between gap-1 text-nowrap text-sm font-medium'
                                }
                            >
                                <span className="shrink-0">Monthly discount</span>
                                <span>
                                    &minus;
                                    <CurrencySymbol />
                                    {maskDecimal(
                                        Math.round(
                                            Math.abs(
                                                plan.annualized_monthly_price - plan.monthly_price,
                                            ) * 100,
                                        ) / 100,
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                    <Separator className="bg-gray-200" />
                    <div className="flex items-start justify-between">
                        <p>Total</p>
                        <div className="text-right">
                            {isGracePeriod && plan.overage_pack_count ? (
                                <>
                                    <p className="text-xl font-medium">
                                        <CurrencySymbol />
                                        {maskDecimal(
                                            showAnnualPricing
                                                ? plan.annualized_monthly_price
                                                : plan.monthly_price,
                                        )}{' '}
                                        <span className="text-lg">/mo</span>
                                    </p>
                                    <p className="text-right text-xs text-gray-600">
                                        for your first month, then <CurrencySymbol />
                                        {maskDecimal(billingCycleCost.perMonth)}
                                        /mo
                                    </p>
                                </>
                            ) : (
                                <p className="text-xl font-medium">
                                    <CurrencySymbol />
                                    {maskDecimal(billingCycleCost.perMonth)}{' '}
                                    <span className="text-lg">/mo</span>
                                </p>
                            )}

                            {showAnnualPricing && (
                                <p className="text-gray-600">
                                    <CurrencySymbol />
                                    {maskDecimal(billingCycleCost.perYear)} billed yearly
                                </p>
                            )}
                            {savingsWithNewPlan > 0 && (
                                <p className="text-success-500 font-medium">
                                    You’ll save <CurrencySymbol />
                                    {maskDecimal(savingsWithNewPlan)}{' '}
                                    {showAnnualPricing ? '/year' : '/mo'}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { SubscriptionPlanLineItemsDetailed };
